/**
 * Client for the public "resend email-verification link" endpoint
 * (`POST /v1/auth/resend-verification`) the Auth0 verify-email gate needs.
 *
 * The endpoint is intentionally NON-ENUMERABLE — a 202 says nothing about
 * whether the address has an account — so the only outcomes we distinguish are
 * sent / invalid-shape / rate-limited / error. We surface a generic
 * "if an account needs verification, we've sent a link" message on `sent`.
 *
 * The URL is derived from the configured GraphQL endpoint (same backend origin)
 * exactly like the docs-upload client, so there's no second env var to keep in
 * sync. `fetch` is injectable for tests.
 */

const RESEND_PATH = '/v1/auth/resend-verification';

export type ResendOutcome = 'sent' | 'invalid' | 'rate_limited' | 'error';

/** Derive the REST resend URL from the app's GraphQL endpoint (same origin). */
export function resendVerificationUrl(graphqlEndpoint: string): string {
  if (graphqlEndpoint.endsWith('/graphql')) {
    return `${graphqlEndpoint.slice(0, -'/graphql'.length)}${RESEND_PATH}`;
  }
  // Not the conventional `/graphql` suffix — append to the configured base so a
  // misconfiguration surfaces as a 404 against an explicit URL, not a silent miss.
  return `${graphqlEndpoint.replace(/\/$/, '')}${RESEND_PATH}`;
}

export interface ResendVerificationDeps {
  /** The app's configured GraphQL endpoint (the resend URL is derived from it). */
  readonly endpoint: string;
  /** Injectable for tests; defaults to global fetch. */
  readonly fetchFn?: typeof fetch;
}

export async function resendVerification(
  email: string,
  deps: ResendVerificationDeps,
): Promise<ResendOutcome> {
  const fetchFn = deps.fetchFn ?? fetch;
  let res: Response;
  try {
    res = await fetchFn(resendVerificationUrl(deps.endpoint), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
  } catch {
    return 'error';
  }
  if (res.status === 202) return 'sent';
  if (res.status === 429) return 'rate_limited';
  if (res.status === 400) return 'invalid';
  return 'error';
}

/**
 * Whether an Auth0 SDK error is the "verify your email" gate (a post-login
 * Action denial).
 *
 * The gate is Auth0's built-in "Require verified email" post-login Action, which
 * denies with a generic OAuth `access_denied`. Crucially, `access_denied` is NOT
 * exclusive to the verify-email gate — other post-login denials (MFA failure, a
 * different tenant Action, an authorization-policy rejection) surface as
 * `access_denied` too. Treating *every* `access_denied` as the verify-email gate
 * (the prior behavior) wrongly shows the "resend verification link" UI for those
 * unrelated denials.
 *
 * There is no finer-grained marker for this gate in the codebase — the Action
 * lives in the Auth0 tenant config, not the repo — so we disambiguate on the
 * `error_description` Auth0 attaches: when a description is present it must name
 * email verification; only a BARE `access_denied` with no description falls back
 * to the verify gate (preserving the prior behavior for that genuinely-unknown
 * case rather than over-claiming on described denials). The user-facing copy is
 * always our own localized text — never this raw message.
 */
export function isEmailVerificationError(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) return false;
  const e = error as { error?: unknown; error_description?: unknown; message?: unknown };
  const description = (
    (typeof e.error_description === 'string' ? e.error_description : '') ||
    (typeof e.message === 'string' ? e.message : '')
  ).toLowerCase();
  // Auth0's default gate message is "Please verify your email before logging in."
  const looksLikeVerifyEmail = description.includes('verify') && description.includes('email');

  if (e.error === 'access_denied') {
    // Described → require it to name email verification; bare → keep prior behavior.
    return description === '' ? true : looksLikeVerifyEmail;
  }
  // Non-OAuth-shaped errors: still honor an explicit verify-email description.
  return looksLikeVerifyEmail;
}
